/**
 * useReauthStatus Hook
 *
 * Polls the global re-authorization status for the current workspace's
 * integrations. Used by the global `<ReauthBanner />` and `<ReauthGate />`
 * to hard-block JTL-dependent pages whenever a workspace integration
 * requires the user to re-confirm new API scopes.
 *
 * Endpoint: GET /api/v1/workspace-settings/integrations/reauth-required
 *
 * Behavior:
 * - Refetches every 60s in the background
 * - Refetches on window focus (catches "I just clicked authorize")
 * - Tolerates endpoint absence: on error we return `needsReauth = false`
 *   so the app stays usable rather than locking everyone out.
 *
 * Max Lines: 50 (Hook size limit)
 */

import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'

export interface ReauthIntegration {
  id: string
  name: string
  integration_type: string
  current_version: string
  available_version: string
}

interface ReauthStatusResponse {
  needs_reauth: boolean
  integrations: ReauthIntegration[]
}

export function useReauthStatus() {
  const query = useQuery<ReauthStatusResponse>({
    queryKey: ['reauth-status'],
    queryFn: async () => {
      const response = await apiClient.get<ReauthStatusResponse>(
        '/api/v1/workspace-settings/integrations/reauth-required',
      )
      return response.data
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: 1,
  })

  return {
    needsReauth: query.data?.needs_reauth ?? false,
    integrations: query.data?.integrations ?? [],
    isLoading: query.isLoading,
  }
}
